import React, { useMemo, useRef, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { perPage, userTablePageSize } from "../../Utility/constants";
import { GtSummaryRequestDto, GtSummaryResponseDto, QuestionResponseDto, Option, TableStyleClasses } from "./Dashboard.Type";
import DashboardService from "./Dashboard.Service";
import { APP_ROUTES } from "../../Libraries/AppConstants";
import StorageHelper from "../../Utility/StorageHelper";
import Search_Icon from "../../assets/images/Search_Icon.svg";
import { buildDashboardColumns, transformDashboardRows } from "./Dashboard.Helper";
import { useDashboardStyles } from "./Dashboard.Style";
import { Pagination } from "@material-ui/lab";
import Menu from "../KycOverride/Menu";
import DashboardFilter from "./DashboardFilter";
import GTPhotoDialogue from "./GTPhotoDialogue";
import excelIcon from "../../assets/images/Excel_Icon.svg";
import Toast from "../../Utility/Toast";
import DashboardTable from "./DashboardTable";

const initialRequest: GtSummaryRequestDto = {
    gtId: "",
    visitId: "",
    projectId: "",
    stateName: "",
    isStateNotMapped: false,
    territoryName: "",
    isTerritoryNotMapped: false,
    districtName: "",
    isDistrictNotMapped: false,
    search: "",
    fromDate: null,
    toDate: null,
    pageNo: 1,
    pageSize: userTablePageSize,
};

export default function GTDashboard() {
    const classes = useDashboardStyles();

    const [request, setRequest] = useState<GtSummaryRequestDto>(initialRequest);
    const [filterRequest, setFilterRequest] = useState<GtSummaryRequestDto>(initialRequest);
    const [searchText, setSearchText] = useState("");
    const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);

    const [stateOptions, setStateOptions] = useState<Option[]>([]);
    const [gtOptions, setGtOptions] = useState<Option[]>([]);
    const [visitOptions, setVisitOptions] = useState<Option[]>([]);
    const [districtOptions, setDistrictOptions] = useState<Option[]>([]);
    const [projectOptions, setprojectOptions] = useState<Option[]>([]);
    const [territoryOptions, setTerritoryOptions] = useState<Option[]>([]);

    const [districtLoading, setDistrictLoading] = useState(false);
    const [territoryLoading, setTerritoryLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [reportDownloading, setReportDownloading] = useState(false);
    const [photoDownloading, setPhotoDownloading] = useState(false);
    const [recordCount, setRecordCount] = useState(0);
    const [questions, setQuestions] = useState<QuestionResponseDto[]>([]);
    const [dashboardData, setDashboardData] = useState<GtSummaryResponseDto[]>([]);

    const requestRef = useRef<GtSummaryRequestDto>(initialRequest);
    const filterRequestRef = useRef<GtSummaryRequestDto>(initialRequest);
    const dashboardDataRef = useRef<GtSummaryResponseDto[]>([]);
    const searchMountedRef = useRef(false);
    const stateOptionsRef = useRef<Option[]>([]);
    const territoryOptionsRef = useRef<Option[]>([]);
    const districtOptionsRef = useRef<Option[]>([]);

    useEffect(() => { requestRef.current = request; }, [request]);
    useEffect(() => { filterRequestRef.current = filterRequest; }, [filterRequest]);
    useEffect(() => { dashboardDataRef.current = dashboardData; }, [dashboardData]);
    useEffect(() => { stateOptionsRef.current = stateOptions; }, [stateOptions]);
    useEffect(() => { territoryOptionsRef.current = territoryOptions; }, [territoryOptions]);
    useEffect(() => { districtOptionsRef.current = districtOptions; }, [districtOptions]);

    const rows = useMemo(() => transformDashboardRows(dashboardData), [dashboardData]);
    const columns = useMemo(() => buildDashboardColumns(questions), [questions]);

    const history = useHistory();

    const handleRequestChange = useCallback((updatedRequest: Partial<GtSummaryRequestDto>) => {
        setRequest((prevRequest) => ({
            ...prevRequest,
            ...updatedRequest,
        }));
    }, []);

    const fetchGtDashboardData = useCallback(async () => {
        setDataLoading(true);
        try {
            const response = await DashboardService.getGtSummary(requestRef.current);
            ReactDOM.unstable_batchedUpdates(() => {
                setDashboardData(response?.results || []);
                setRecordCount(response?.totalRecords || 0);
                setDataLoading(false);
            });
            if (!response?.results || response.results.length === 0) {
                Toast.info("No Records Found");
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                StorageHelper.removeAll();
                document.cookie.split(";").forEach(function (c) {
                    document.cookie = c
                        .replace(/^ +/, "")
                        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                });
                history.replace(APP_ROUTES.LOGIN);
            } else {
                Toast.error("Failed to fetch dashboard data");
                ReactDOM.unstable_batchedUpdates(() => {
                    setDashboardData([]);
                    setDataLoading(false);
                });
            }
        }
    }, [history]);

    const fetchGtQuestions = useCallback(async () => {
        try {
            const response = await DashboardService.getGtQuestions();
            setQuestions(response);
        } catch (error: any) {
            Toast.error("Failed to load questions");
        }
    }, []);

    const handleApplyFilters = useCallback(() => {
        const filters = filterRequestRef.current;
        setRequest(prev => ({
            ...prev,
            ...filters,
            stateName: filters.isStateNotMapped
                ? stateOptionsRef.current.map(x => x.label).join(",")
                : filters.stateName,
            territoryName: filters.isTerritoryNotMapped
                ? territoryOptionsRef.current.map(x => x.label).join(",")
                : filters.territoryName,
            districtName: filters.isDistrictNotMapped
                ? districtOptionsRef.current.map(x => x.label).join(",")
                : filters.districtName,
            pageNo: 1,
        }));
    }, []);

    const handleResetFilters = useCallback(() => {
        searchMountedRef.current = false;
        setSearchText("");
        setFilterRequest({ ...initialRequest });
        setRequest({ ...initialRequest });
    }, []);

    const handleFilterRequestChange = useCallback((changes: Partial<GtSummaryRequestDto>) => {
        setFilterRequest(prev => ({
            ...prev,
            ...changes,
        }));
    }, []);

    useEffect(() => {
        if (!searchMountedRef.current) {
            searchMountedRef.current = true;
            return;
        }
        const handler = setTimeout(() => {
            if (searchText.length >= 3 || searchText.length === 0) {
                handleRequestChange({ search: searchText, pageNo: 1 });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchText, handleRequestChange]);

    useEffect(() => {
        fetchGtDashboardData();
    }, [request, fetchGtDashboardData]);

    useEffect(() => {
        fetchGtQuestions();
    }, [fetchGtQuestions]);

    const handleUnauthorized = useCallback(() => {
        StorageHelper.removeAll();
        document.cookie.split(";").forEach(function (c) {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        history.replace(APP_ROUTES.LOGIN);
    }, [history]);

    useEffect(() => {
        const loadStates = async () => {
            try {
                const statesRes = await DashboardService.getStates();
                setStateOptions(statesRes);
            } catch (error: any) {
                if (error.response?.status === 401) {
                    handleUnauthorized();
                } else {
                    Toast.error("Failed to load states");
                }
            }
        };
        loadStates();
    }, [handleUnauthorized]);

    useEffect(() => {
        const loadProjects = async () => {
            try {
                //  const projectsRes = await DashboardService.getProjects();
                //setprojectOptions(projectsRes);
                setprojectOptions([]);
            } catch (error: any) {
                if (error.response?.status === 401) {
                    handleUnauthorized();
                } else {
                    Toast.error("Failed to load projects");
                }
            }
        };
        loadProjects();
    }, [handleUnauthorized]);

    // Load GT List (depends on selected State / Territory / District filters)
    useEffect(() => {
        const loadGtList = async () => {
            const hasFilter = !!(
                filterRequest.stateName ||
                filterRequest.isStateNotMapped ||
                filterRequest.territoryName ||
                filterRequest.isTerritoryNotMapped ||
                filterRequest.districtName ||
                filterRequest.isDistrictNotMapped
            );
            if (!hasFilter) {
                setGtOptions([]);
                return;
            }
            try {
                const gtRes = await DashboardService.getGtList({
                    stateName: filterRequest.isStateNotMapped
                        ? stateOptions.map(x => x.label).join(",")
                        : filterRequest.stateName,
                    isStateNotMapped: filterRequest.isStateNotMapped,
                    territoryName: filterRequest.isTerritoryNotMapped
                        ? territoryOptions.map(x => x.label).join(",")
                        : filterRequest.territoryName,
                    isTerritoryNotMapped: filterRequest.isTerritoryNotMapped,
                    districtName: filterRequest.isDistrictNotMapped
                        ? districtOptions.map(x => x.label).join(",")
                        : filterRequest.districtName,
                    isDistrictNotMapped: filterRequest.isDistrictNotMapped,
                });
                setGtOptions(gtRes);
            } catch (error: any) {
                if (error.response?.status === 401) {
                    handleUnauthorized();
                } else {
                    Toast.error("Failed to load GT list");
                }
            }
        };
        loadGtList();
    }, [
        filterRequest.stateName,
        filterRequest.isStateNotMapped,
        filterRequest.territoryName,
        filterRequest.isTerritoryNotMapped,
        filterRequest.districtName,
        filterRequest.isDistrictNotMapped,
        stateOptions,
        territoryOptions,
        districtOptions,
        handleUnauthorized,
    ]);

    // Load Teritory
    useEffect(() => {
        const loadTerritory = async () => {
            if (!filterRequest.stateName) {
                setTerritoryOptions([]);
                return;
            }
            setTerritoryLoading(true);
            try {
                const stateNames = filterRequest.stateName
                    .split(",")
                    .map(state => state.trim())
                    .filter(Boolean);
                const stateIds = stateOptions
                    .filter(state => stateNames.includes(state.label))
                    .map(state => state.value);
                const response = await DashboardService.getStateWiseTerritory(stateIds);
                setTerritoryOptions(response);
            } catch (error: any) {
                Toast.error("Failed to load territories");
            } finally {
                setTerritoryLoading(false);
            }
        }
        loadTerritory();
    }, [filterRequest.stateName]);
    //Load Districts
    useEffect(() => {
        const loadDistrict = async () => {
            if (!filterRequest.territoryName) {
                setDistrictOptions([]);
                return;
            }
            setDistrictLoading(true);
            try {
                const territoryNames = filterRequest.territoryName
                    .split(",")
                    .map(territory => territory.trim())
                    .filter(Boolean);
                const territoryIds = territoryOptions
                    .filter(territory => territoryNames.includes(territory.label))
                    .map(territory => territory.value);
                const response = await DashboardService.getDistrict(territoryIds);
                setDistrictOptions(response);
            } catch (error: any) {
                Toast.error("Failed to load districts");
            } finally {
                setDistrictLoading(false);
            }
        };
        loadDistrict();
    }, [filterRequest.territoryName]);

    // Load Visits
    useEffect(() => {
        const loadVisits = async () => {
            if (!filterRequest.gtId) {
                setVisitOptions([]);
                return;
            }
            try {
                const response = await DashboardService.getVisitByGt(filterRequest.gtId);
                setVisitOptions(response);
            } catch (error: any) {
                Toast.error(error?.message || "Failed to load visits");
            }
        };
        loadVisits();
    }, [filterRequest.gtId]);

    const handleDownloadImage = useCallback(async (visitId: string, fileName: string) => {
        try {
            const { base64Content, fileExtension, fileName: downloadedFileName } = await DashboardService.downloadImage(visitId, fileName);

            // Convert Base64 to Blob
            const byteCharacters = atob(base64Content);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            const mimeType =
                fileExtension.toLowerCase() === ".png"
                    ? "image/png"
                    : fileExtension.toLowerCase() === ".jpg" ||
                        fileExtension.toLowerCase() === ".jpeg"
                        ? "image/jpeg"
                        : "application/octet-stream";

            const blob = new Blob([byteArray], { type: mimeType });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = downloadedFileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Toast.error(error?.message || "Failed to download photos");
        }
    }, []);

    const handleDownloadReport = useCallback(async () => {
        setReportDownloading(true);
        try {
            const blob = await DashboardService.downloadReport(requestRef.current);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            const formattedDate = today.toLocaleDateString("en-GB").replace(/\//g, "-");

            const fileName = `GT_Summary_Report_${formattedDate}.xlsx`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (error : any) {
            Toast.error( error?.message || "Failed to download report");
        } finally {
            setReportDownloading(false);
        }
    }, []);

    const handleOpenPhotoDialog = useCallback(() => {
        setIsPhotoDialogOpen(true);
    }, []);

    const handleDownloadBulkImage = useCallback(async (visitIds: string[]) => {
        setPhotoDownloading(true);
        try {
            const blob = await DashboardService.downloadBulkImage(visitIds);

            if (blob?.type === 'application/json' || blob?.type === 'text/plain') {
                const text = await blob.text();
                const errorData = JSON.parse(text);

                throw new Error(errorData?.error || "Server returned an error");
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            const formattedDate = today.toLocaleDateString("en-GB").replace(/\//g, "-");
            link.setAttribute('download', `GT_Photos_${formattedDate}.zip`);
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            Toast.error(error?.message || "Failed to download photos");
        } finally {
            setPhotoDownloading(false);
        }
    }, []);

    return (
        <div className="container min-w-full mt-20">
            <>
                <div className="flex px-5 py-3">
                    <h1 className="text-xl md:text-3xl font-bold">Ground Truthing Dashboard</h1>
                </div>
                <hr className="my-1 border-[1.5px]" />
                <div className="min-w-full">
                    <div className="container px-5 py-2 min-w-full">
                        <DashboardFilter
                            request={filterRequest}
                            stateOptions={stateOptions}
                            territoryOptions={territoryOptions}
                            districtOptions={districtOptions}
                            projectOptions={projectOptions}
                            gtOptions={gtOptions}
                            visitOptions={visitOptions}
                            districtLoading={districtLoading}
                            territoryLoading={territoryLoading}
                            onRequestChange={handleFilterRequestChange}
                            onApply={handleApplyFilters}
                            onReset={handleResetFilters}
                            onDownload={handleOpenPhotoDialog}
                            isDataEmpty={dashboardData.length === 0}
                            photoDownloading={photoDownloading}
                        />
                        <GTPhotoDialogue
                            open={isPhotoDialogOpen}
                            onClose={() => setIsPhotoDialogOpen(false)}
                            onDownload={handleDownloadBulkImage}
                        />
                    </div>
                    <div className="m-3 mt-6 border-2 border-gray-300 bg-[#E5F5F6]">
                        <div className="block sm:flex bg-[#E5F5F6]">
                            <div className="search-container my-3 mx-2">
                                <input
                                    type="text"
                                    placeholder="GT Name, GT ID, Visit ID"
                                    style={{ fontSize: "12px" }}
                                    className="search-input min-w-[100%] sm:min-w-[125%]"
                                    value={searchText}
                                    onChange={(e: any) => {
                                        setSearchText(e.target.value);
                                    }}
                                />
                                <div className="search-icon">
                                    <img src={Search_Icon} alt="Search_Icon" />
                                </div>
                            </div>
                            <div className="flex ml-auto gap-3 items-center px-3">
                                <p className="text-[#009CA7] text-sm pb-2 ml-auto font-bold">
                                    Count - {recordCount}
                                </p>
                                <button
                                    className={`flex items-center gap-2 text-[#009CA7] text-sm font-bold bg-white border border-[#009CA7] px-3 py-1.5 rounded-md transition-colors ${dashboardData.length === 0 || reportDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                    onClick={dashboardData.length === 0 || reportDownloading ? undefined : handleDownloadReport}
                                    disabled={dashboardData.length === 0 || reportDownloading}
                                >
                                    <img src={excelIcon} alt="Export" className="w-5 h-5" />
                                    {reportDownloading ? "Exporting..." : "Export"}
                                </button>
                            </div>
                        </div>
                        <hr className="border border-gray-200" />
                        <DashboardTable
                            rows={rows}
                            columns={columns}
                            dataLoading={dataLoading}
                            classes={classes as unknown as TableStyleClasses}
                            onDownloadImage={handleDownloadImage
                            }
                        />
                        <div className="flex flex-col bg-[#F4F4F7] md:flex md:flex-row items-center justify-center py-5">
                            <Pagination
                                count={dashboardData.length > 0 ? Math.ceil(recordCount / request.pageSize) : 0}
                                disabled={dashboardData.length === 0}
                                page={request.pageNo}
                                shape="rounded"
                                onChange={(event, value) => {
                                    handleRequestChange({ pageNo: value });
                                }}
                            />
                            <Menu
                                pageSize={request.pageSize}
                                changeDefaultTotal={(number) => {
                                    handleRequestChange({ pageSize: number, pageNo: 1 });
                                }}
                                perPage={perPage}
                            />
                        </div>
                    </div>
                </div>
            </>
        </div>
    );
}
